/**
 * AvatarUpload — Profile Image Upload Component
 *
 * Self-contained component that handles:
 *  - Click to select image
 *  - Image preview before upload
 *  - Upload to /api/v1/users/avatar (multipart)
 *  - Displays current avatar or initials fallback
 *  - Max 5MB, accepts jpg/png/webp
 *
 * Usage:
 *   <AvatarUpload
 *     currentAvatar={profile?.avatar}
 *     firstName={profile?.first_name}
 *     lastName={profile?.last_name}
 *     onUploaded={(newUrl) => setProfile(p => ({...p, avatar: newUrl}))}
 *   />
 */

import React, { useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Camera } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const initials = (first = '', last = '') =>
    [(first || '')[0], (last || '')[0]]
        .filter(Boolean)
        .map(c => c.toUpperCase())
        .join('') || '?';

const AvatarUpload = ({
    currentAvatar,
    firstName = '',
    lastName  = '',
    size      = 96,        // px
    onUploaded,            // (newAvatarUrl: string) => void
    disabled  = false,
}) => {
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const handleClick = () => {
        if (disabled || uploading) return;
        inputRef.current?.click();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate type
        if (!['image/jpeg','image/png','image/webp'].includes(file.type)) {
            toast.error('Only JPG, PNG, or WebP images are allowed');
            return;
        }

        // Validate size — max 5MB
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be smaller than 5MB');
            return;
        }

        // Show local preview immediately
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(file);

        // Upload
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const res = await axiosInstance.post('/users/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const newUrl = res.data.data?.avatar_url || res.data.data?.url || res.data.url;
            toast.success('Profile photo updated!');
            onUploaded?.(newUrl);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Upload failed');
            setPreview(null); // revert preview on error
        } finally {
            setUploading(false);
            // Reset input so same file can be re-selected
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const avatarSrc = preview || currentAvatar;
    const borderRadius = size;

    return (
        <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
            {/* Avatar circle */}
            <div
                onClick={handleClick}
                style={{
                    width: size, height: size,
                    borderRadius: '50%',
                    background: avatarSrc ? 'transparent' : '#4f46e5',
                    cursor: disabled ? 'default' : 'pointer',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: Math.round(size * 0.33),
                    fontWeight: 700,
                    color: '#fff',
                    border: '3px solid #e5e7eb',
                    transition: 'opacity 0.2s',
                    opacity: uploading ? 0.6 : 1,
                }}
                title={disabled ? '' : 'Click to change photo'}>
                {avatarSrc ? (
                    <img
                        src={avatarSrc}
                        alt="Profile"
                        style={{ width:'100%', height:'100%', objectFit:'cover' }}
                    />
                ) : (
                    initials(firstName, lastName)
                )}
            </div>

            {/* Camera icon overlay */}
            {!disabled && (
                <div
                    onClick={handleClick}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: Math.round(size * 0.34),
                        height: Math.round(size * 0.34),
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}>
                    {uploading
                        ? <Spinner size="sm" animation="border" variant="primary"
                            style={{ width:Math.round(size*0.18), height:Math.round(size*0.18) }}/>
                        : <Camera size={Math.round(size * 0.17)} color="#4f46e5"/>}
                </div>
            )}

            {/* Hidden file input */}
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display:'none' }}
                onChange={handleFile}
            />
        </div>
    );
};

export default AvatarUpload;
